import { expireSession, sessionStorageService } from "../services/session.storage";

const BASE = process.env.REACT_APP_API_BASE || "";

async function request(path, options = {}) {
    const url = `${BASE}${path}`;
    const token = sessionStorageService.get();

    const res = await fetch(url, {
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...options.headers,
        },
        ...options,
    });

    let body = null;
    try {
        body = await res.json();
    } catch {
    }

    if (!res.ok) {
        if (res.status === 401 && token) expireSession();
        const message =
            body?.message || body?.error || `Request failed (${res.status})`;
        throw new Error(message);
    }

    return body;
}

/**
 * Like request(), but for multipart/form-data bodies (file uploads) - must
 * NOT set Content-Type manually, since the browser needs to add its own
 * boundary parameter to it.
 */
async function requestMultipart(path, formData) {
    const url = `${BASE}${path}`;
    const token = sessionStorageService.get();

    const res = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
    });

    let body = null;
    try {
        body = await res.json();
    } catch {
    }

    if (!res.ok) {
        if (res.status === 401 && token) expireSession();
        const message =
            body?.message || body?.error || `Request failed (${res.status})`;
        throw new Error(message);
    }

    return body;
}

/**
 * HTTP client for all authentication and account endpoints. request()
 * automatically attaches an Authorization: Bearer header whenever a
 * token is present, so every call made through this module (including
 * protected endpoints like /api/ai/**) is authenticated for free.
 */
export const authenticationApi = {
    login: (email, password) =>
        request("/api/auth/login", {
            method: "POST",
            body: JSON.stringify({ email, password }),
        }),

    /** Step 1: validates the email is free and sends it a 6-digit code. Returns { otpRequired: true }. */
    signup: (name, email, password) =>
        request("/api/auth/signup", {
            method: "POST",
            body: JSON.stringify({ fullName: name, email, password }),
        }),

    /** Step 2: confirms the code and actually creates the account. Returns { token, email }. */
    verifySignup: (name, email, password, otp) =>
        request("/api/auth/signup/verify", {
            method: "POST",
            body: JSON.stringify({ fullName: name, email, password, otp }),
        }),

    /**
     * Send OTP to any identifier (email or +91xxxxxxxxxx).
     * Uses URLSearchParams to avoid double-encoding the + sign.
     */
    sendOtpToIdentifier: (identifier) => {
        const params = new URLSearchParams({ identifier });
        return request(`/api/auth/otp/send?${params}`, { method: "POST" });
    },

    /**
     * Verify OTP for any identifier.
     * Returns { token, email } on success.
     */
    verifyOtpForIdentifier: (identifier, otp) =>
        request("/api/auth/otp/verify", {
            method: "POST",
            body: JSON.stringify({ email: identifier, otp }),
        }),

    /**
     * For the general user login/signup page (LoginForm/SignupForm via
     * SocialAuthButtons). Uses the "google-user" registration so it's
     * routed to GoogleUserAuthSuccessHandler, not the broker flow.
     *
     * `returnState` is this route's own location.state (redirectTo/
     * pendingQuery/openGuidedSearch/entryPoint -- the same shape
     * AuthenticationPage.jsx's handleSuccess already reads for the
     * password/OTP path), passed through as an opaque JSON query param.
     * Google's OAuth round trip is a real browser navigation away from the
     * SPA and back, so React Router's own location.state is lost entirely;
     * the backend stashes this string in a cookie and echoes it back
     * verbatim on the final /auth?token=... redirect. Found live: without
     * this, "Continue with Google" from the "Talk to Me" pill's login gate
     * landed on the plain homepage instead of resuming the guided search.
     */
    googleOAuthUrl: (returnState) => {
        const qs = returnState && Object.keys(returnState).length
            ? `?returnState=${encodeURIComponent(JSON.stringify(returnState))}`
            : "";
        return `${BASE}/oauth2/authorization/google-user${qs}`;
    },

    /** Settings modal: fetch the verified profile (name/email/avatar). */
    getCurrentUser: () => request("/api/auth/me"),

    /** Sends a password reset email if an account exists for that address. */
    forgotPassword: (email) =>
        request("/api/auth/forgot-password", {
            method: "POST",
            body: JSON.stringify({ email }),
        }),

    /** Completes a password reset using the token from the emailed link. */
    resetPassword: (token, newPassword) =>
        request("/api/auth/reset-password", {
            method: "POST",
            body: JSON.stringify({ token, newPassword }),
        }),

    /** Settings modal: save editable profile fields. */
    updateProfile: (fullName) =>
        request("/api/auth/me", {
            method: "PUT",
            body: JSON.stringify({ fullName }),
        }),

    /**
     * Change (or, for Google/OTP accounts with no password yet, set for the
     * first time) the current user's password. currentPassword may be
     * omitted when the account doesn't have one yet (see hasPassword on
     * getCurrentUser()'s response).
     */
    changePassword: (currentPassword, newPassword) =>
        request("/api/auth/me/password", {
            method: "PUT",
            body: JSON.stringify({ currentPassword, newPassword }),
        }),

    /** Settings modal: upload a new avatar image. Returns the updated profile. */
    uploadAvatar: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return requestMultipart("/api/auth/me/avatar", formData);
    },

    /** Saved/favorite properties. */
    listFavorites: () => request("/api/auth/me/favorites"),
    addFavorite: (propertyId) =>
        request(`/api/auth/me/favorites/${propertyId}`, { method: "POST" }),
    removeFavorite: (propertyId) =>
        request(`/api/auth/me/favorites/${propertyId}`, { method: "DELETE" }),

    /**
     * Persisted AI chat history (per logged-in account). Distinct from the
     * ephemeral, per-chatId conversational-context cache the AI endpoints
     * use internally - this is the durable "my past chats" list.
     */
    listChatHistory: () => request("/api/ai/chat-history"),
    saveChatSession: ({ propertyId, title, preview, messages, source, clientChatId }) =>
        request("/api/ai/chat-history", {
            method: "POST",
            body: JSON.stringify({ propertyId, title, preview, messages, source, clientChatId }),
        }),
    /** Overwrites an existing session (e.g. an ongoing conversation) rather than creating a new row. */
    updateChatSession: (id, { propertyId, title, preview, messages, source, clientChatId }) =>
        request(`/api/ai/chat-history/${id}`, {
            method: "PUT",
            body: JSON.stringify({ propertyId, title, preview, messages, source, clientChatId }),
        }),
    deleteChatSession: (id) => request(`/api/ai/chat-history/${id}`, { method: "DELETE" }),
    pinChatSession: (id, pinned) =>
        request(`/api/ai/chat-history/${id}/pin`, { method: "PUT", body: JSON.stringify({ pinned }) }),
    /** Creates the public link, or refreshes its snapshot when one exists. Returns { token, sharedAt }. */
    shareChatSession: (id) => request(`/api/ai/chat-history/${id}/share`, { method: "POST" }),
    unshareChatSession: (id) => request(`/api/ai/chat-history/${id}/share`, { method: "DELETE" }),
    /** Public read of a shared chat -- no login needed. */
    getSharedChat: (token) => request(`/api/ai/shared/${encodeURIComponent(token)}`),
    clearChatHistory: () => request("/api/ai/chat-history", { method: "DELETE" }),
    /**
     * Fetches the PDF export of a saved chat session as a blob (not JSON,
     * so this bypasses request() rather than extending it for one caller).
     */
    downloadChatSessionPdf: async (id) => {
        const token = sessionStorageService.get();
        const res = await fetch(`${BASE}/api/ai/chat-history/${id}/pdf`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);

        const disposition = res.headers.get("Content-Disposition") || "";
        const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] || `chat-${id}.pdf`;
        return { blob: await res.blob(), filename };
    },
};